import { useCallback, useState } from 'react';
import { formatJsonContent, JsonFormatResult } from '../lib/json-formatter';

/**
 * Hook for managing JSON formatting functionality
 * Provides state management and formatting methods for JSON content
 */
export function useJsonFormatter() {
	const [isFormatting, setIsFormatting] = useState(false);
	const [lastResult, setLastResult] = useState<JsonFormatResult | null>(null);

	/**
	 * Format JSON content with error handling and state management
	 *
	 * @param content - The content to format
	 * @returns Promise<JsonFormatResult> - The formatting result
	 */
	const formatJson = useCallback(async (content: string): Promise<JsonFormatResult> => {
		setIsFormatting(true);

		try {
			const result = formatJsonContent(content);
			setLastResult(result);
			return result;
		} catch (error) {
			const errorResult: JsonFormatResult = {
				success: false,
				error: 'Unexpected error during formatting',
			};
			setLastResult(errorResult);
			return errorResult;
		} finally {
			setIsFormatting(false);
		}
	}, []);

	/**
	 * Clear the last formatting result
	 */
	const clearResult = useCallback(() => {
		setLastResult(null);
	}, []);

	/**
	 * Reset all state
	 */
	const reset = useCallback(() => {
		setIsFormatting(false);
		setLastResult(null);
	}, []);

	return {
		formatJson,
		isFormatting,
		lastResult,
		clearResult,
		reset,
	};
}
